const DENIED_BROWSER_STORAGE = 1;
const initializedModals = new WeakSet();

function browserStorageAllowed() {
  // Match ecomm-ui's CookieAcceptance enum: DENIED is 1 and an unset value defaults to FULL.
  return window.Mhecomm_BrowserStorage !== DENIED_BROWSER_STORAGE;
}

function getCookieValue(name) {
  if (!browserStorageAllowed()) return null;

  const cookies = document.cookie ? document.cookie.split(';') : [];
  for (const cookie of cookies) {
    const trimmedCookie = cookie.trim();
    if (!trimmedCookie.startsWith(`${name}=`)) continue;

    try {
      return decodeURIComponent(trimmedCookie.substring(name.length + 1));
    } catch (error) {
      if (error instanceof URIError) return null;
      throw error;
    }
  }
  return null;
}

function setCookieValue(name, value, options = {}) {
  if (!browserStorageAllowed()) return;
  if (!name) throw new Error('Cookie name is required');

  const { daysToExpire, path = '/', domain, secure = false, sameSite = 'Lax' } = options;
  let cookie = `${name}=${encodeURIComponent(value ?? '')}`;

  if (typeof daysToExpire === 'number') {
    const date = new Date();
    date.setTime(date.getTime() + daysToExpire * 24 * 60 * 60 * 1000);
    cookie += `; expires=${date.toUTCString()}`;
  }
  if (domain) cookie += `; domain=${domain}`;
  cookie += `; path=${path}`;
  if (secure) cookie += '; secure';
  if (sameSite) cookie += `; samesite=${sameSite}`;

  document.cookie = cookie;
}

function parseRegionRoots(modal) {
  const regionRootsElement = modal.hasAttribute('data-region-roots')
    ? modal
    : modal.querySelector('[data-region-roots]');
  const rawValue = regionRootsElement?.getAttribute('data-region-roots');
  if (!rawValue) return [];

  try {
    const parsed = JSON.parse(rawValue);
    const roots = Array.isArray(parsed) ? parsed : parsed?.roots;
    return Array.isArray(roots) ? roots.filter((root) => typeof root === 'string' && root.trim().length) : [];
  } catch (error) {
    if (error instanceof SyntaxError) return [];
    throw error;
  }
}

function findRegionalUrlForCountry(links, countryValue) {
  if (!countryValue || countryValue === 'US') return null;

  const normalizedCountry = countryValue.trim().toLowerCase();
  const matchingLink = links.find((link) => (link.hreflang || '').toLowerCase().includes(normalizedCountry));
  return matchingLink?.href ?? null;
}

function currentPathContainsRegionRoot(roots) {
  const pathname = window.location.pathname.toLowerCase();
  return roots.some((root) => {
    if (!root) return false;
    const normalizedRoot = root.toLowerCase();
    const candidate = normalizedRoot.startsWith('/') ? normalizedRoot : `/${normalizedRoot}`;
    return pathname.startsWith(candidate);
  });
}

export function initializeRegionalPageModal() {
  if (!browserStorageAllowed()) return;

  const modal = document.querySelector('#regional-page-modal');
  if (!modal || initializedModals.has(modal)) return;

  const links = [...document.querySelectorAll('link[rel="alternate"]')].map((link) => ({
    hreflang: link.getAttribute('hreflang'),
    href: link.href,
  }));
  const viewerCountry = getCookieValue('viewerCountryFromCDN');
  const modalDismissed = getCookieValue('regionalPageModalDismissed') === 'true';
  const modalInstance = new window.bootstrap.Modal(modal);
  initializedModals.add(modal);
  const regionalUrl = findRegionalUrlForCountry(links, viewerCountry);
  const changeLink = modal.querySelector('#regional-change-link');
  const regionRoots = parseRegionRoots(modal);

  if (!regionalUrl || modalDismissed || !changeLink) return;

  changeLink.setAttribute('href', regionalUrl);
  if (currentPathContainsRegionRoot(regionRoots)) return;

  modalInstance.toggle();

  const dismissHandler = () => {
    setCookieValue('regionalPageModalDismissed', 'true', { daysToExpire: 30, path: '/' });
    modal.removeEventListener('hidden.bs.modal', dismissHandler);
  };
  modal.addEventListener('hidden.bs.modal', dismissHandler);
}

function registerRegionalPageModal() {
  const initialize = () => initializeRegionalPageModal();
  if (document.readyState === 'loading' || document.readyState === 'interactive') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    queueMicrotask(initialize);
  }
}

registerRegionalPageModal();
